import React from 'react';
import { Box, Typography, styled } from '@mui/material';

type SeveridadeType = 'erro' | 'alerta' | 'sucesso' | 'informativo' | string;

interface BadgeSeveridadeProps {
  severidade: SeveridadeType;
}

const BadgeContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'badgeColor',
})<{ badgeColor: { bg: string; text: string; border: string } }>(({ badgeColor }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: '2px 8px',
  borderRadius: '8px',
  backgroundColor: badgeColor.bg,
  color: badgeColor.text,
  border: `1px solid ${badgeColor.border}`,
  fontWeight: 600,
  fontSize: '0.7rem',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
}));

const Dot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'dotColor',
})<{ dotColor: string }>(({ dotColor }) => ({
  width: 5,
  height: 5,
  borderRadius: '50%',
  backgroundColor: dotColor,
  marginRight: 6,
}));

const getColors = (sev: SeveridadeType) => {
  const norm = sev.toLowerCase();
  switch (norm) {
    case 'erro':
    case 'critico':
    case 'critical':
    case 'error':
      return { bg: 'rgba(239, 68, 68, 0.1)', text: '#EF4444', border: 'rgba(239, 68, 68, 0.2)', dot: '#EF4444' };
    case 'alerta':
    case 'warning':
    case 'attention':
    case 'atencao':
      return { bg: 'rgba(245, 158, 11, 0.1)', text: '#F59E0B', border: 'rgba(245, 158, 11, 0.2)', dot: '#F59E0B' };
    case 'sucesso':
    case 'success':
    case 'concluido':
      return { bg: 'rgba(16, 185, 129, 0.1)', text: '#10B981', border: 'rgba(16, 185, 129, 0.2)', dot: '#10B981' };
    case 'informativo':
    case 'info':
    case 'information':
    default:
      return { bg: 'rgba(59, 130, 246, 0.1)', text: '#3B82F6', border: 'rgba(59, 130, 246, 0.2)', dot: '#3B82F6' };
  }
};

const getLabel = (sev: SeveridadeType) => {
  const norm = sev.toLowerCase();
  switch (norm) {
    case 'erro':
    case 'critico':
    case 'critical':
    case 'error':
      return 'Erro';
    case 'alerta':
    case 'warning':
    case 'attention':
    case 'atencao':
      return 'Alerta';
    case 'sucesso':
    case 'success':
    case 'concluido':
      return 'Sucesso';
    case 'informativo':
    case 'info':
    case 'information':
    default:
      return 'Info';
  }
};

export const BadgeSeveridade: React.FC<BadgeSeveridadeProps> = ({ severidade }) => {
  const colors = getColors(severidade);
  const label = getLabel(severidade);

  return (
    <BadgeContainer badgeColor={colors}>
      <Dot dotColor={colors.dot} />
      <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.7rem', color: colors.text }}>
        {label}
      </Typography>
    </BadgeContainer>
  );
};

export default BadgeSeveridade;
